import { useState } from 'react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface Participant {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'owner' | 'member' | 'guest';
  display_name: string | null;
  avatar_url: string | null;
  alias: string | null;
  created_at: string;
}

interface ParticipantListProps {
  participants: Participant[];
  loading: boolean;
  currentUserId?: string;
  onRemoveParticipant: (userId: string) => Promise<void>;
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

function getRoleBadgeVariant(role: string): 'primary' | 'info' | 'neutral' {
  switch (role) {
    case 'owner':
      return 'primary';
    case 'member':
      return 'info';
    case 'guest':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function ParticipantList({
  participants,
  loading,
  currentUserId,
  onRemoveParticipant,
}: ParticipantListProps) {
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const handleRemove = async () => {
    if (!confirmRemoveId) return;
    try {
      setRemovingUserId(confirmRemoveId);
      await onRemoveParticipant(confirmRemoveId);
    } finally {
      setRemovingUserId(null);
      setConfirmRemoveId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-slate-400" data-testid="participant-list-loading">
        <div className="inline-block w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
        <p className="mt-2 text-sm">Loading participants...</p>
      </div>
    );
  }

  if (participants.length === 0) {
    return (
      <div className="p-4 text-center text-slate-400" data-testid="participant-list-empty">
        <p className="text-sm">No participants found.</p>
      </div>
    );
  }

  return (
    <div data-testid="participant-list">
      <ul className="divide-y divide-slate-700/50">
        {participants.map((participant) => {
          const displayName =
            participant.display_name || participant.alias || participant.user_id.slice(0, 8);
          const isCurrentUser = participant.user_id === currentUserId;
          const isRemoving = removingUserId === participant.user_id;

          return (
            <li
              key={participant.user_id}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-700/30 transition-colors"
              data-testid={`participant-${participant.user_id}`}
            >
              {/* Avatar */}
              {participant.avatar_url ? (
                <img
                  src={participant.avatar_url}
                  alt={displayName}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-slate-300">
                    {getInitials(displayName)}
                  </span>
                </div>
              )}

              {/* Name and role */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white truncate">
                    {displayName}
                    {isCurrentUser && (
                      <span className="text-slate-400 font-normal ml-1">(you)</span>
                    )}
                  </span>
                  <Badge variant={getRoleBadgeVariant(participant.role)} size="sm" style="soft">
                    {participant.role}
                  </Badge>
                </div>
                {participant.alias && participant.display_name && (
                  <p className="text-xs text-slate-400 truncate">@{participant.alias}</p>
                )}
              </div>

              {/* Remove button (not shown for owners or current user) */}
              {!isCurrentUser && participant.role !== 'owner' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmRemoveId(participant.user_id)}
                  disabled={isRemoving}
                  className="flex-shrink-0 text-xs"
                  data-testid={`remove-participant-${participant.user_id}`}
                >
                  {isRemoving ? 'Removing...' : 'Remove'}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Remove Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmRemoveId !== null}
        title="Remove Participant"
        message="Are you sure you want to remove this participant from the conversation?"
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleRemove}
        onCancel={() => setConfirmRemoveId(null)}
      />
    </div>
  );
}
